var _a, _b, _c, _d, _e, _f, _g;
import { __decorate, __metadata, __param } from "tslib";
import { injectable, inject } from 'inversify';
import { GlobalProxy, SmartFilterProxy, PatentListProxy, ModelContextType, PatentListProvideType, PatentListSourceType, ContextProxy, } from '@patsnap/hiro-event-proxy';
import { HiroBasicApi } from '../../apis/hiro-basic-api';
import { PageSelectorServiceSymbol } from '@/presentation/components/page-selector/PageSelectorService';
import { useFolderSetting } from '@/presentation/pages/workspace-detail/composables/use-folder-setting';
import { useDetailRoute } from '@/presentation/pages/workspace-detail/composables/use-detail-route';
import { DetailServiceSymbol } from '@/services/DetailService';
import psHttp from '@patsnap/common-ps-http';
import { i18nSymbol } from '@/i18n';
import _ from 'lodash';
import { RouterService } from '@cores/services/vue-router-service';
import { useSmartFilter } from '@/domains/hiro/composables/use-smart-filter';
import { SmartFilterRepository } from '@/domains/hiro/repositories/smart-filter-repository';
import { create } from '@patsnap/tahiti-core';
import { RavenInsertMeta } from '@cores/services/tracker/sentry';
let HiroEntryServiceImpl = class HiroEntryServiceImpl {
    constructor(_i18n, _hiroBasicApi, _globalProxy, _contextProxy, _patentListProxy, _smartFilterProxy, _pageSelectorService, _detailService, _routerService, _smartFilterRepository) {
        this._i18n = _i18n;
        this._hiroBasicApi = _hiroBasicApi;
        this._globalProxy = _globalProxy;
        this._contextProxy = _contextProxy;
        this._patentListProxy = _patentListProxy;
        this._smartFilterProxy = _smartFilterProxy;
        this._pageSelectorService = _pageSelectorService;
        this._detailService = _detailService;
        this._routerService = _routerService;
        this._smartFilterRepository = _smartFilterRepository;
        this._hiroLoadDeferred = create();
        this._hiroBasicConfigPromise = null;
        /**
         * 获取专利列表上下文
         */
        this._getPatentListContext = async () => {
            // 获取选中的专利
            const [isSelectedPatent, selectedLength] = this._isSelectedPatent();
            if (isSelectedPatent) {
                // 有选中专利时，提供选中的专利
                const { currentSort } = useFolderSetting();
                const { qid, efqId } = useDetailRoute();
                return {
                    provideType: PatentListProvideType.Select,
                    selectedLength,
                    queryPatents: {
                        type: PatentListSourceType.Workspace,
                        full_path: this._routerService.currentRoute.fullPath,
                        workspace_id: this._detailService.spaceId || '',
                        folder_id: this._detailService.folderId || '',
                        count: selectedLength,
                        mode: this._pageSelectorService.mode,
                        patent_ids: this._pageSelectorService.finalIds,
                        qid: qid.value,
                        efqid: efqId.value,
                        start: this._pageSelectorService.rangeFrom,
                        end: this._pageSelectorService.rangeTo,
                        sort: currentSort.value,
                    },
                };
            }
            else {
                // 没有选中专利时，提供当前页面的专利
                const currentPagePatents = this._getCurrentPagePatents();
                const selectedLength = currentPagePatents.length;
                return {
                    provideType: PatentListProvideType.CurrentPage,
                    selectedLength,
                    queryPatents: {
                        type: PatentListSourceType.Workspace,
                        full_path: this._routerService.currentRoute.fullPath,
                        workspace_id: this._detailService.spaceId || '',
                        folder_id: this._detailService.folderId || '',
                        mode: 'INCLUDE',
                        patent_ids: currentPagePatents.map(patent => this._getPatentId(patent)),
                        count: selectedLength,
                    },
                };
            }
        };
        this._setupRpcHandlers();
        this._detailService.onLoaded = () => {
            this._tryApplyFilter();
            this._detailService.onLoaded = _.noop;
        };
        this._globalProxy.eventProxy.on('loaded', () => {
            this._hiroLoadDeferred.resolve(true);
        });
        this._pageSelectorService.onStateChanged(() => {
            this._updateContext();
        });
    }
    async getHiroBasicConfig() {
        if (this._hiroBasicConfigPromise) {
            return this._hiroBasicConfigPromise;
        }
        this._hiroBasicConfigPromise = this._hiroBasicApi.getHiroBasic()
            .then(res => {
            // 开发环境 vs 生产环境的 URL 配置
            const hiroUrl = process.env.VUE_APP_MF_HIRO;
            return {
                name: 'micro-hiro-workspace',
                url: hiroUrl,
                enabled: !!res.copilot_flag,
                enabledAgentCodes: Object.entries(res.agent_flag)
                    .filter(([_, value]) => value)
                    .map(([key]) => key),
                agentConfigs: res.agent_config ?? {},
            };
        });
        return this._hiroBasicConfigPromise;
    }
    getWujieProps(basicConfig) {
        return {
            locale: this._i18n.locale,
            messages: this._i18n.messages,
            httpRequest: psHttp,
            proxy: {
                global: this._globalProxy,
                context: this._contextProxy,
                patentList: this._patentListProxy,
                smartFilter: this._smartFilterProxy,
            },
            enabledAgentCodes: basicConfig.enabledAgentCodes,
            agentConfigs: basicConfig.agentConfigs,
            tracker: {
                track: RavenInsertMeta,
            },
        };
    }
    _setupRpcHandlers() {
        this._patentListProxy.rpcHost.register('getPatentListPageInfo', () => {
            return {
                type: PatentListSourceType.Workspace,
                fullPath: this._routerService.currentRoute.fullPath,
            };
        });
        this._patentListProxy.rpcHost.register('restorePatentListPage', async (pageInfo) => {
            const url = new URL(pageInfo.fullPath, window.location.origin);
            const params = url.searchParams;
            if (params.get('spaceId') !== this._detailService.spaceId) {
                // 不在当前空间, 无法直接恢复
                return {
                    isRestoredOnCurrentPage: false,
                };
            }
            if (pageInfo.fullPath !== this._routerService.currentRoute.fullPath) {
                await this._routerService.push(pageInfo.fullPath).catch(_.noop);
                // 添加专利接口调用结束时的监听
                const deferred = create();
                this._detailService.onLoaded = () => {
                    deferred.resolve(true);
                    this._detailService.onLoaded = _.noop;
                };
                await deferred.promise;
            }
            return {
                // hiro 会 apply filter 到当前页面
                isRestoredOnCurrentPage: true,
            };
        });
        this._smartFilterProxy.rpcHost.register('appendSmartFilterResult', async (result) => {
            // 智能过滤结果处理
            // 通过 useSmartFilter 处理智能过滤结果
            const { appendSmartFilterResult } = useSmartFilter();
            appendSmartFilterResult(result, true);
        });
    }
    async _updateContext() {
        await this._hiroLoadDeferred.promise;
        // 获取当前页专利数据作为建议上下文
        const currentPagePatents = this._getCurrentPagePatents();
        const suggestionContexts = this._convertPatentsToModelContexts(currentPagePatents, ModelContextType.PN);
        this._contextProxy.rpcHiro.call('patchContextState', {
            suggestionContext: {
                contexts: suggestionContexts,
            },
        }, 'all');
        // 设置专利列表上下文
        const patentListContext = await this._getPatentListContext();
        this._contextProxy.rpcHiro.call('setPatentListContext', patentListContext, 'all');
    }
    /**
     * 是否选择了专利，以及选择的数量
     */
    _isSelectedPatent() {
        // 全选、范围模式
        if (this._pageSelectorService.isSelectAllMode || this._pageSelectorService.isRangeMode) {
            return [true, this._pageSelectorService.selectedCount];
        }
        else {
            // 普通选择模式
            if (this._pageSelectorService.finalIds.length > 0) {
                return [true, this._pageSelectorService.selectedCount];
            }
            else {
                // 当前页
                return [false, 0];
            }
        }
    }
    /**
     * 获取当前页面的专利数据
     */
    _getCurrentPagePatents() {
        try {
            // 从 PageSelectorService 获取当前页面的专利ID
            const sourceIds = this._pageSelectorService.sourceIds;
            if (sourceIds.length === 0) {
                return [];
            }
            // 从 DetailService 获取专利详情数据
            const patentsData = this._detailService.patentsData;
            const patents = patentsData.patents || [];
            // 根据当前页面的ID筛选专利
            return patents.filter(patent => sourceIds.includes(this._getPatentId(patent)));
        }
        catch (error) {
            console.error('Error getting current page patents:', error);
            return [];
        }
    }
    /**
     * 将专利数据转换为模型上下文格式
     */
    _convertPatentsToModelContexts(patents, type) {
        return patents.map((patent) => ({
            type,
            value: patent.standard?.PATENT_ID || patent.PATENT_ID,
            label: patent.standard?.PN || patent.PN,
            description: patent.standard?.TITLE || patent.TITLE,
        }));
    }
    _getPatentId(patent) {
        return patent.standard?.PATENT_ID || patent.PATENT_ID;
    }
    async _tryApplyFilter() {
        const urlParams = new URLSearchParams(window.location.search);
        const filterId = urlParams.get('append_sfq');
        if (filterId) {
            // 移除参数
            urlParams.delete('append_sfq');
            const newUrl = urlParams.toString() ?
                `${window.location.pathname}?${urlParams.toString()}` :
                window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            // 调用智能过滤
            const results = await this._smartFilterRepository.getSmartFilterResults([filterId]);
            const result = _.first(results);
            if (result) {
                const { appendSmartFilterResult } = useSmartFilter();
                appendSmartFilterResult(result, true);
            }
        }
    }
};
HiroEntryServiceImpl = __decorate([
    injectable(),
    __param(0, inject(i18nSymbol)),
    __param(1, inject(HiroBasicApi)),
    __param(2, inject(GlobalProxy)),
    __param(3, inject(ContextProxy)),
    __param(4, inject(PatentListProxy)),
    __param(5, inject(SmartFilterProxy)),
    __param(6, inject(PageSelectorServiceSymbol)),
    __param(7, inject(DetailServiceSymbol)),
    __param(8, inject(RouterService)),
    __param(9, inject(SmartFilterRepository)),
    __metadata("design:paramtypes", [Object, typeof (_a = typeof HiroBasicApi !== "undefined" && HiroBasicApi) === "function" ? _a : Object, typeof (_b = typeof GlobalProxy !== "undefined" && GlobalProxy) === "function" ? _b : Object, typeof (_c = typeof ContextProxy !== "undefined" && ContextProxy) === "function" ? _c : Object, typeof (_d = typeof PatentListProxy !== "undefined" && PatentListProxy) === "function" ? _d : Object, typeof (_e = typeof SmartFilterProxy !== "undefined" && SmartFilterProxy) === "function" ? _e : Object, Object, Object, typeof (_f = typeof RouterService !== "undefined" && RouterService) === "function" ? _f : Object, typeof (_g = typeof SmartFilterRepository !== "undefined" && SmartFilterRepository) === "function" ? _g : Object])
], HiroEntryServiceImpl);
export { HiroEntryServiceImpl };
